import { computed, ref, watchEffect } from 'vue';
import { useForm } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { toTypedSchema } from '@vee-validate/yup';
import * as Yup from 'yup';
import type {
  CampaignInterval,
  ConfidenceInterval,
  OpportunityDetail,
  OpportunityDetailModel,
  OpportunityDetailModelKeypaths,
  OpportunityType,
  OpportunityUpdateBody,
  OpportunityUpdatePayload,
  OptionsUser,
  Swimlane,
} from '@apparatix/types/Leads';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunities } from './useOpportunities';
import { useDateFns } from './useDateFns';
import { AppStore } from '@apparatix/stores/AppStore.ts';

/**
 *
 * @deprecated This file is only used to back the legacy detail editor, which the create opportunity wizard still uses.
 * new work should not use or import this composable. Use useOpportunityDetailEditor instead
 */
export const useOpportunityDetails = (type: OpportunityType, detail?: OpportunityDetail) => {
  const store = useOpportunityStore();
  const {
    getConfidence,
    getInterval,
    getStatusById,
    getOpportunityOwnerOrDefault,
    standardizeObjectForPutRequest,
    updateOpportunity,
  } = useOpportunities();

  const {
    areEqual, isValidDate, parse,
  } = useDateFns();

  const defaultStatus = store.swimlanes[type][0];

  // This exists to manage currentUser when store loaded while using CORE
  if (!store.currentUser) {
    store.currentUser = {
      id: AppStore.currentUser.id,
      fullName: AppStore.currentUser.displayName,
      userId: AppStore.currentUser.username,
      officeId: AppStore.currentUser.officeId,
      isActive: AppStore.currentUser.isActive,
    };
  }

  const defaultOffice = store.options[type].offices.find(office => office.id === store.currentUser.officeId);

  const defaultModel: OpportunityDetailModel = {
    opportunity: {
      type: type,
      owner: store.currentUser.fullName,
      ownerId: store.currentUser.id,
      status: defaultStatus.status,
      statusId: defaultStatus.id,
      swimlaneId: defaultStatus.id,
      temperature: store.options[type].temperatures[0],
      expectedClose: null,
      office: defaultOffice,
      anticipatedStart: null,
      dueDate: null,
      budget: null,
      notes: '',
      name: '',
      lostReasons: [],
    },
    revenueData: {
      audience: [],
      rate: null,
      value: null,
      interval: null,
      periods: null,
      inventoryArea: '',
    },
    planData: {
      campaignObjective: '',
      statedNeeds: '',
      solutionsAndStrategy: '',
      miscNotes: '',
    },
  };

  const dateValidator = Yup.date()
    .transform((value, _, ctx) => {
      if (ctx.isType(value)) return value;
      if (typeof value === 'string') {
        const date = parse(value);
        if (date) {
          return date;
        }
      }

      return null;
    })
    .test({
      name: 'is-valid-date',
      message: 'Is not a date',
      test: value => {
        if (!value) {
          return true;
        }
        if (!(value instanceof Date)) {
          return false;
        }
        return isValidDate(value);
      },
    })
    .typeError('Invalid date')
    .notRequired()
    .default(null);

  const numberValidator = Yup.number()
    .min(0, 'Must be positive')
    .typeError('Invalid number')
    .notRequired()
    .default(null);

  const validationSchema = Yup.object().shape({
    expectedClose: dateValidator,
    anticipatedStart: dateValidator,
    dueDate: dateValidator,
    budget: numberValidator,
    value: numberValidator,
    revenueData: Yup.object().shape({
      rate: numberValidator,
      value: numberValidator,
      periods: numberValidator,
    }),
  });

  const state = ref<OpportunityDetailModel>({
    opportunity: { ...defaultModel.opportunity, ...detail?.opportunity },
    revenueData: { ...defaultModel.revenueData, ...detail?.revenueData },
    planData: { ...defaultModel.planData, ...detail?.planData },
  });

  // This should probably pass the status id rather than swimlane id, I think we have it here
  const status = ref<Swimlane>(getStatusById(type, state.value.opportunity.swimlaneId));
  watchEffect(() => {
    if (!status.value) return;
    if (status.value.id !== state.value.opportunity.swimlaneId) {
      state.value.opportunity.swimlaneId = status.value.id;
      state.value.opportunity.statusId = status.value.id;
      state.value.opportunity.status = status.value.status;
    }
  });

  const interval = ref<CampaignInterval>(getInterval(type, state.value.revenueData.interval));
  watchEffect(() => {
    if (!interval.value) return;
    state.value.revenueData.interval = interval.value.selectionValue;
  });

  const owner = ref<OptionsUser>(getOpportunityOwnerOrDefault(type, state.value.opportunity.ownerId));
  watchEffect(() => {
    if (!owner.value) return;
    state.value.opportunity.ownerId = owner.value.id;
    state.value.opportunity.owner = owner.value.fullName;
  });

  const ci = ref<ConfidenceInterval>(getConfidence(type, state.value.opportunity?.ci));
  watchEffect(() => {
    if (!ci.value) return;
    state.value.revenueData.ci = ci.value.id;
  });

  const saveTimeoutId = ref<number>();

  const geoSelectionUrl = computed(() => {
    if (!state.value.opportunity.id) return null;
    return `/apx/TAG/campaigns/campaignGeographicSelection.html?id=${state.value.opportunity.id}`;
  });

  const audienceSelectionUrl = computed(() => {
    if (!state.value.opportunity.id) return null;
    return `/apx/TAG/campaigns/campaignAudienceSelection.html?id=${state.value.opportunity.id}`;
  });

  const isCreatingNewOpportunity = computed(() => isNaN(state.value.opportunity.id));

  const {
    defineField, errors, isValidating, meta, setFieldError,
  } = useForm({
    validationSchema: toTypedSchema(validationSchema),
    initialValues: {
      expectedClose: state.value.opportunity.expectedClose as Date,
      anticipatedStart: state.value.opportunity.anticipatedStart as Date,
      dueDate: state.value.opportunity.dueDate as Date,
      budget: state.value.opportunity.budget,
      value: state.value.opportunity.value,
      revenueData: {
        rate: state.value.revenueData.rate,
        value: state.value.revenueData.value,
        periods: state.value.revenueData.periods,
      },
    },
    validateOnMount: true,
  });

  const [expectedClose, expectedCloseProps] = defineField('expectedClose');
  const [anticipatedStart, anticipatedStartProps] = defineField('anticipatedStart');
  const [dueDate, dueDateProps] = defineField('dueDate');
  const [budget, budgetProps] = defineField('budget');
  const [rate, rateProps] = defineField('revenueData.rate');
  const [value, valueProps] = defineField('value');
  const [periods, periodsProps] = defineField('revenueData.periods');

  watchEffect(() => {
    if (!errors.value.expectedClose && expectedClose.value !== state.value.opportunity.expectedClose) {
      state.value.opportunity.expectedClose = expectedClose.value;
    }

    if (!errors.value.anticipatedStart && anticipatedStart.value !== state.value.opportunity.anticipatedStart) {
      state.value.opportunity.anticipatedStart = anticipatedStart.value;
    }

    if (!errors.value.dueDate && dueDate.value !== state.value.opportunity.dueDate) {
      state.value.opportunity.dueDate = dueDate.value;
    }

    if (!errors.value.budget && budget.value !== state.value.opportunity.budget) {
      state.value.opportunity.budget = budget.value;
    }

    if (!errors.value['revenueData.rate'] && rate.value !== state.value.revenueData.rate) {
      state.value.revenueData.rate = rate.value;
    }

    if (!errors.value.value && value.value !== state.value.opportunity.value) {
      state.value.opportunity.value = value.value;
    }

    if (!errors.value['revenueData.periods'] && periods.value !== state.value.revenueData.periods) {
      state.value.revenueData.periods = periods.value;
    }
  });

  // Form validation will prevent allowing the user to enter a date in the past. However if their record ALREADY has a date in the past,
  // this has the unwanted side effect of not allowing them to update the form. This function checks to see if the dates on the form
  // are identical to the date already on the object, and if so, manually clears the errors on those fields so that the form can submit
  const markExistingPastDatesAsValid = () => {
    if (meta.value.valid) return;
    if (errors.value.anticipatedStart) {
      if (areEqual(anticipatedStart.value, store.selectedOpportunity[type]?.opportunity?.anticipatedStart)) {
        setFieldError('anticipatedStart', undefined);
      }
    }
    if (errors.value.dueDate) {
      if (areEqual(dueDate.value, store.selectedOpportunity[type]?.opportunity?.dueDate)) {
        setFieldError('dueDate', undefined);
      }
    }
    if (errors.value.expectedClose) {
      if (areEqual(expectedClose.value, store.selectedOpportunity[type]?.opportunity?.expectedClose)) {
        setFieldError('expectedClose', undefined);
      }
    }
  };

  // This slight bit of weirdness is because vee-validate doesn't run validation synchronously, so after clearing date errors, meta.value.valid still === false
  // for a short amount of time. Unfortunately vee-validate made the baffling choice to not make the validation awaitable even thought it's async, so we have
  // to make our own little wrapper so that we can await validation completion
  const waitForValidation = (): Promise<void> => new Promise((resolve, reject) => {
    let retryCount = 0;
    const retryInterval = window.setInterval(() => {
      // This prevents some unforeseen problem where maybe isValidating never gets set to false for some reason,
      // in which case this would run forever and lock up the UI. So we wait a max of one second and then just give up
      if (retryCount >= 100) {
        window.clearInterval(retryInterval);
        reject();
      }
      if (!isValidating.value) {
        window.clearInterval(retryInterval);
        resolve();
      } else {
        retryCount += 1;
      }
    }, 10);
  });

  const saveFieldChange = async (fieldPath: OpportunityDetailModelKeypaths) => {
    markExistingPastDatesAsValid();
    await waitForValidation();
    if (!meta.value.valid) return;
    clearTimeout(saveTimeoutId.value);

    saveTimeoutId.value = window.setTimeout(() => sendOpportunityUpdate(fieldPath), 1000);
  };

  const sendOppUpdate = async (formUpdates) => {
    const payload: OpportunityUpdatePayload = { data: { opportunity: [formUpdates]}};

    await updateOpportunity(payload);
  };


  const sendOpportunityUpdate = async (fieldPath: OpportunityDetailModelKeypaths) => {
    // @TODO: review diff between this update and above update
    return;
    if (!state.value.opportunity.id) return;

    const [rootField, field] = fieldPath.split('.') as [OpportunityDetailModelKeypaths, OpportunityDetailModelKeypaths];
    const tmpState = cloneDeep(state.value);

    const updateBody: OpportunityUpdateBody = { id: state.value.opportunity.id };

    standardizeObjectForPutRequest(tmpState[rootField]);

    if (rootField === 'opportunity') {
      if (fieldPath === 'opportunity.status') {
        updateBody[field] = status.value.id;
        if (tmpState['opportunity']['reasons']) {
          updateBody['opportunityLostReasons'] = tmpState['opportunity']['reasons'];
          state.value.opportunity.lostReasons = store.options[type].opportunityLostReasons.filter(
            reason => tmpState['opportunity']['reasons'].includes(reason.id),
          ).map(reason => reason.tag);
        }
      } else {
        updateBody[field] = tmpState[rootField][field];
      }
    }

    if (rootField === 'revenueData') {
      updateBody.revenueData = {};
      if (fieldPath === 'revenueData.ci') {
        updateBody.revenueData.ci = ci.value.id;
      } else {
        updateBody.revenueData[field] = tmpState[rootField][field];
      }
    }

    if (rootField === 'planData') {
      updateBody.planData = {};
      updateBody.planData[field] = tmpState[rootField][field];
    }

    const payload: OpportunityUpdatePayload = { data: { opportunity: [updateBody]}};

    await updateOpportunity(payload);
  };

  return {
    anticipatedStart,
    anticipatedStartProps,
    audienceSelectionUrl,
    budget,
    budgetProps,
    ci,
    dueDate,
    dueDateProps,
    errors,
    expectedClose,
    expectedCloseProps,
    geoSelectionUrl,
    interval,
    isCreatingNewOpportunity,
    owner,
    periods,
    periodsProps,
    rate,
    rateProps,
    saveFieldChange,
    sendOppUpdate,
    state,
    status,
    value,
    valueProps,
  };
};
